import React, { useState, useEffect } from 'react';
import { 
  Box, Paper, Typography, Grid, TextField, MenuItem, Button, 
  Dialog, DialogTitle, DialogContent, DialogActions, LinearProgress, Alert, Divider 
} from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import api from '../../api';

const GeneratePayroll = () => {
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  const [month, setMonth] = useState(currentMonth);
  const [year, setYear] = useState(currentYear);
  
  const [payrollExists, setPayrollExists] = useState(false);
  const [regenerationReason, setRegenerationReason] = useState('');
  
  // Modal & Progress State
  const [modalOpen, setModalOpen] = useState(false);
  const [progress, setProgress] = useState({ hold: 0, calc: 0, sheet: 0 });
  const [statusMsg, setStatusMsg] = useState('Initializing...');
  const [isComplete, setIsComplete] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const months = [
    "January", "February", "March", "April", "May", "June", 
    "July", "August", "September", "October", "November", "December"
  ];

  const years = [currentYear - 1, currentYear, currentYear + 1];

  // 1. Check if payroll exists whenever Month or Year changes
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const res = await api.get(`/payroll/check-status?month=${month}&year=${year}`);
        setPayrollExists(res.data.exists);
        if (!res.data.exists) setRegenerationReason('');
      } catch (err) {
        console.error("Failed to check payroll status", err);
      }
    };
    checkStatus();
  }, [month, year]);

  // 2. Trigger Generation API
  const handleGenerate = async () => {
    setModalOpen(true);
    setIsComplete(false);
    setHasError(false);
    setProgress({ hold: 0, calc: 0, sheet: 0 });
    setStatusMsg('Checking eligible employees & running Auto-Hold Engine...');

    // Simulate progress animation while waiting for backend
    let simProgress = 0;
    const simInterval = setInterval(() => {
      simProgress += 5;
      if (simProgress <= 30) {
        setProgress(p => ({ ...p, hold: simProgress * 3.3 })); // Fill hold bar
      } else if (simProgress <= 70) {
        setStatusMsg('Calculating attendance, salaries, and statutory deductions...');
        setProgress(p => ({ ...p, hold: 100, calc: (simProgress - 30) * 2.5 })); // Fill calc bar
      } else if (simProgress <= 90) {
        setStatusMsg('Generating official DataSheet Excel snapshot...');
        setProgress(p => ({ ...p, calc: 100, sheet: (simProgress - 70) * 4 })); // Fill sheet bar
      }
    }, 500);

    try {
      const adminName = localStorage.getItem('emp_name') || 'Admin User';
      
      const payload = {
        month: month,
        year: year,
        is_regeneration: payrollExists,
        regeneration_reason: payrollExists ? regenerationReason : null,
        generated_by: adminName
      };

      const res = await api.post('/payroll/generate', payload);

      // Success: Snap all to 100%
      clearInterval(simInterval);
      setProgress({ hold: 100, calc: 100, sheet: 100 });
      setStatusMsg(res.data.message);
      setIsComplete(true);
      setPayrollExists(true);
      setRegenerationReason('');

    } catch (err) {
      clearInterval(simInterval);
      setHasError(true);
      const detail = err.response?.data?.detail || err.message;
      setErrorMsg(`Failed: ${detail}`);
      setStatusMsg('Process halted due to an error.');
    }
  };

  const handleCloseModal = () => {
    if (isComplete || hasError) {
      setModalOpen(false);
    }
  };

  // UI Component for the Progress Bars
  const ProgressBar = ({ label, value }) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body2" fontWeight="bold">{label}</Typography>
        <Typography variant="body2" color="textSecondary">{Math.round(value)}%</Typography>
      </Box>
      <LinearProgress 
        variant="determinate" 
        value={value} 
        color={hasError ? "error" : (value === 100 ? "success" : "primary")} 
        sx={{ height: 8, borderRadius: 4 }} 
      />
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4 }}>
      <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold', mb: 3 }}>
        Payroll Generation
      </Typography>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Grid container spacing={4}>
          
          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Payroll Month</Typography>
            <TextField 
              select fullWidth size="small" 
              value={month} 
              onChange={(e) => setMonth(e.target.value)}
            >
              {months.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Payroll Year</Typography>
            <TextField 
              select fullWidth size="small" 
              value={year} 
              onChange={(e) => setYear(e.target.value)}
            >
              {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Target Application</Typography>
            <TextField 
              fullWidth size="small" 
              value="Full Company" 
              disabled 
              helperText="Payroll is officially generated for the entire active company workforce."
            />
          </Grid>

          {/* Regeneration Alert & Reason Box */}
          {payrollExists && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Payroll for <strong>{month} {year}</strong> has already been generated. Proceeding will overwrite the existing payroll and DataSheet.
              </Alert>
              <TextField 
                fullWidth size="small" required
                label="Mandatory Regeneration Reason" 
                placeholder="e.g., Updated salary structure for Employee TG1005"
                value={regenerationReason} 
                onChange={(e) => setRegenerationReason(e.target.value)}
              />
            </Grid>
          )}

          <Grid item xs={12} sx={{ textAlign: 'right', mt: 2 }}>
            <Button 
              variant="contained" 
              color={payrollExists ? "warning" : "primary"}
              startIcon={payrollExists ? <ReplayIcon /> : <PlayCircleOutlineIcon />}
              onClick={handleGenerate}
              disabled={payrollExists && regenerationReason.trim() === ''}
              sx={{ px: 4, py: 1.5, fontWeight: 'bold' }}
            >
              {payrollExists ? 'Regenerate Payroll' : 'Generate Payroll'}
            </Button>
          </Grid>

        </Grid>
      </Paper>

      {/* 3-Step Progress Modal */}
      <Dialog open={modalOpen} maxWidth="sm" fullWidth disableEscapeKeyDown>
        <DialogTitle sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {payrollExists ? 'Regenerating Payroll...' : 'Generating Payroll...'}
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 4 }}>
          
          {hasError && <Alert severity="error" sx={{ mb: 3 }}>{errorMsg}</Alert>}
          {!hasError && isComplete && <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>{statusMsg}</Alert>}
          {!hasError && !isComplete && <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>{statusMsg}</Typography>}

          <ProgressBar label="1. Run Auto-Hold Salary Engine" value={progress.hold} />
          <ProgressBar label="2. Calculate Payroll & Statutory Rules" value={progress.calc} />
          <ProgressBar label="3. Generate Excel DataSheet" value={progress.sheet} />

        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            onClick={handleCloseModal} 
            disabled={!isComplete && !hasError}
            color={hasError ? "error" : "primary"}
            sx={{ px: 5 }}
          >
            {hasError ? 'Close' : 'OK'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GeneratePayroll;